import { useState, useRef } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { PublicShell } from "@/components/PublicShell"
import { Button } from "@/components/ui/button"
import { Plus } from "lucide-react"

type SelectOpt = { id: number; name?: string; title?: string }
type RecordingFormProps = {
  id: number | null
  title: string
  from_album: string | null
  recordable_type: string
  recordable_id: number | null
  recordable_name: string | null
  record_label_id: number | null
  recording_type: string | null
  catalogue_number: string | null
  comments: string | null
  released_year: number | null
  released_month: number | null
  first_charted: string | null
  highest_position: number | null
  weeks_in_chart: number | null
  purchase_link_url: string | null
  purchase_link_text: string | null
  meta_title: string | null
  meta_description: string | null
  meta_keywords: string | null
}
type PageProps = {
  recording: RecordingFormProps
  record_labels: SelectOpt[]
  bands?: SelectOpt[]
  people?: SelectOpt[]
  return_to?: string | null
  errors?: Record<string, string>
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]

function InlineLabelAdd({ onAdded, inputClass }: { onAdded: (rl: SelectOpt) => void; inputClass: string }) {
  const [open, setOpen]     = useState(false)
  const [name, setName]     = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState("")
  const nameRef             = useRef<HTMLInputElement>(null)

  function reset() { setName(""); setError("") }

  async function handleAdd() {
    const trimmed = name.trim()
    if (!trimmed) return
    setSaving(true)
    setError("")
    try {
      const res = await fetch("/record_labels", {
        method:      "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept":       "application/json",
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "",
        },
        body: JSON.stringify({ record_label: { name: trimmed, content: "" } }),
      })
      const data = await res.json()
      if (res.ok) {
        onAdded(data as SelectOpt)
        reset()
        setOpen(false)
      } else {
        setError(data.errors?.name ?? "Failed to create label")
      }
    } catch {
      setError("Failed to create label")
    } finally {
      setSaving(false)
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setOpen(true); setTimeout(() => nameRef.current?.focus(), 50) }}
        className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 hover:underline mt-1.5"
      >
        <Plus className="h-3 w-3" /> Add new label
      </button>
    )
  }

  return (
    <div className="mt-2 rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800/50 p-3 space-y-2">
      <p className="text-xs font-medium text-stone-500 dark:text-stone-400 mb-1">New record label</p>
      <input
        ref={nameRef}
        type="text"
        placeholder="Label name *"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); handleAdd() }
          if (e.key === "Escape") { setOpen(false); reset() }
        }}
        className={inputClass}
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={handleAdd}
          disabled={saving || !name.trim()}
          className="text-xs font-medium text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-50 px-3 py-1.5 rounded transition-colors"
        >
          {saving ? "Saving…" : "Create"}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); reset() }}
          className="text-xs text-stone-500 hover:text-stone-700 dark:hover:text-stone-300"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

export default function RecordingForm({
  recording,
  record_labels,
  bands,
  people,
  return_to,
}: {
  recording: RecordingFormProps
  record_labels: SelectOpt[]
  bands?: SelectOpt[]
  people?: SelectOpt[]
  return_to?: string | null
}) {
  const { errors = {} } = usePage<PageProps>().props
  const isNew = recording.id === null
  const recordableLocked = !!(recording.recordable_id && recording.recordable_name)

  const [labelOptions, setLabelOptions] = useState<SelectOpt[]>(record_labels)

  const [form, setForm] = useState({
    title: recording.title ?? "",
    from_album: recording.from_album ?? "",
    recordable_type: recording.recordable_type ?? "Band",
    recordable_id: recording.recordable_id !== null ? String(recording.recordable_id) : "",
    record_label_id: recording.record_label_id !== null ? String(recording.record_label_id) : "",
    recording_type: recording.recording_type ?? "",
    catalogue_number: recording.catalogue_number ?? "",
    comments: recording.comments ?? "",
    released_year: recording.released_year !== null ? String(recording.released_year) : "",
    released_month: recording.released_month !== null ? String(recording.released_month) : "",
    first_charted: recording.first_charted ?? "",
    highest_position: recording.highest_position !== null ? String(recording.highest_position) : "",
    weeks_in_chart: recording.weeks_in_chart !== null ? String(recording.weeks_in_chart) : "",
    purchase_link_url: recording.purchase_link_url ?? "",
    purchase_link_text: recording.purchase_link_text ?? "",
    meta_title: recording.meta_title ?? "",
    meta_description: recording.meta_description ?? "",
    meta_keywords: recording.meta_keywords ?? "",
  })

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const payload = return_to ? { recording: form, return_to } : { recording: form }
    if (isNew) router.post("/recordings", payload)
    else router.patch(`/recordings/${recording.id}`, payload)
  }

  const recordableOptions = form.recordable_type === "Band" ? (bands ?? []) : (people ?? [])
  const cancelHref = return_to ?? "/recordings"
  const pageTitle = isNew ? "New recording" : `Edit ${recording.title}`
  const ic = "w-full rounded border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-1 focus:ring-amber-500"

  return (
    <>
      <Head title={pageTitle}>
        <meta name="description" content={pageTitle} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageTitle} />
      </Head>
      <PublicShell>
        <div className="max-w-xl">
          <h1 className="mb-6">{pageTitle}</h1>

          <form onSubmit={handleSubmit} className="space-y-4">

            <div>
              <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Title</label>
              <input name="title" type="text" value={form.title} onChange={handleChange} className={ic} />
              {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
            </div>

            {/* Recordable — locked when pre-set from a band/person page */}
            {recordableLocked ? (
              <div>
                <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Artist / Band</label>
                <div className={`${ic} bg-stone-50 dark:bg-stone-800/50 text-stone-400 cursor-default flex items-center gap-2`}>
                  <span className="flex-1">{recording.recordable_name}</span>
                  <span className="text-xs text-stone-400">{recording.recordable_type}</span>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Artist / Band type</label>
                  <select name="recordable_type" value={form.recordable_type} onChange={handleChange} className={ic}>
                    <option value="Band">Band</option>
                    <option value="Person">Person</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Artist / Band</label>
                  <select name="recordable_id" value={form.recordable_id} onChange={handleChange} className={ic}>
                    <option value="">— select —</option>
                    {recordableOptions.map((o) => <option key={o.id} value={o.id}>{o.name ?? o.title}</option>)}
                  </select>
                  {errors.recordable && <p className="mt-1 text-xs text-red-600">{errors.recordable}</p>}
                </div>
              </>
            )}

            <div>
              <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Record label</label>
              <select name="record_label_id" value={form.record_label_id} onChange={handleChange} className={ic}>
                <option value="">— none —</option>
                {labelOptions.map((rl) => <option key={rl.id} value={rl.id}>{rl.name}</option>)}
              </select>
              <InlineLabelAdd onAdded={(rl) => {
                setLabelOptions((prev) => [...prev, rl].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")))
                setForm((prev) => ({ ...prev, record_label_id: String(rl.id) }))
              }} inputClass={ic} />
            </div>

            <div>
              <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Recording type</label>
              <input name="recording_type" type="text" value={form.recording_type} onChange={handleChange} className={ic} placeholder="Single, Album, EP…" />
            </div>

            <div>
              <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">From album (singles)</label>
              <input name="from_album" type="text" value={form.from_album} onChange={handleChange} className={ic} placeholder="e.g. Kick (1987)" />
            </div>

            <div>
              <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Catalogue number</label>
              <input name="catalogue_number" type="text" value={form.catalogue_number} onChange={handleChange} className={ic} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Released year</label>
                <input name="released_year" type="number" value={form.released_year} onChange={handleChange} className={ic} />
              </div>
              <div>
                <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Released month</label>
                <select name="released_month" value={form.released_month} onChange={handleChange} className={ic}>
                  <option value="">— month —</option>
                  {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">First charted</label>
              <input name="first_charted" type="date" value={form.first_charted} onChange={handleChange} className={ic} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Highest position</label>
                <input name="highest_position" type="number" min="1" value={form.highest_position} onChange={handleChange} className={ic} />
              </div>
              <div>
                <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Weeks in chart</label>
                <input name="weeks_in_chart" type="number" min="1" value={form.weeks_in_chart} onChange={handleChange} className={ic} />
              </div>
            </div>

            <div>
              <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Comments</label>
              <textarea name="comments" rows={4} value={form.comments} onChange={handleChange} className={ic} />
            </div>

            <div>
              <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Purchase link URL</label>
              <input name="purchase_link_url" type="url" value={form.purchase_link_url} onChange={handleChange} className={ic} />
            </div>

            <div>
              <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Purchase link text</label>
              <input name="purchase_link_text" type="text" value={form.purchase_link_text} onChange={handleChange} className={ic} placeholder="Buy / Stream" />
            </div>

            <div className="border-t border-stone-100 dark:border-stone-800 pt-4">
              <p className="text-xs text-stone-400 uppercase tracking-wide mb-3">SEO</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Meta title</label>
                  <input name="meta_title" type="text" value={form.meta_title} onChange={handleChange} className={ic} />
                </div>
                <div>
                  <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Meta description</label>
                  <input name="meta_description" type="text" value={form.meta_description} onChange={handleChange} className={ic} />
                </div>
                <div>
                  <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Meta keywords</label>
                  <input name="meta_keywords" type="text" value={form.meta_keywords} onChange={handleChange} className={ic} />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit">{isNew ? "Create recording" : "Update recording"}</Button>
              <Link href={cancelHref} className="text-sm text-stone-500 no-underline hover:text-stone-900 dark:hover:text-white">Cancel</Link>
            </div>
          </form>
        </div>
      </PublicShell>
    </>
  )
}
